import React, { useState, createElement } from 'react';
import { ClipboardCopyIcon, DownloadIcon, CheckIcon } from 'lucide-react';
import { calculateBasePrice } from './pricingUtils';
import { calculateAdderTotal } from './AdderSelector';
import type { WindowData } from './WindowRow';
interface ExportActionsProps {
  windows: WindowData[];
  productLine: string;
  totalBasePrice: number;
  totalAdderCost: number;
  grandTotal: number;
  discountPercent: number;
  discountAmount: number;
  totalDue: number;
  totalWindowCount: number;
  avgPerWindow: number;
}
function fmt(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}
// Abbreviation map for operation names in export
const OP_ABBR: Record<string, string> = {
  'Single Hung': 'SH',
  'Double Hung': 'DH',
  'Sliding Glass Door': 'SGD',
  Picture: 'Pic'
};
function abbrevOp(op: string): string {
  return OP_ABBR[op] ?? op;
}
function pad(str: string, width: number, alignRight = false): string {
  if (alignRight) return str.padStart(width);
  return str.padEnd(width);
}
function buildPlainText(props: ExportActionsProps): string {
  const {
    windows,
    productLine,
    totalBasePrice,
    totalAdderCost,
    grandTotal,
    discountPercent,
    discountAmount,
    totalDue,
    totalWindowCount
  } = props;
  const lines: string[] = [];
  lines.push(`WINDOW QUOTE — ${productLine.toUpperCase()}`);
  lines.push(`Date: ${new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })}`);
  lines.push('');
  const T = '\t';
  // Header — fewer columns by combining width×height into "Size"
  lines.push(`Qty${T}Size${T}Sq Ft${T}Op${T}Obs${T}Add-Ons${T}Adder ${T}Total`);
  lines.push('');
  const included = windows.filter(w => w.included);
  included.forEach(win => {
    const sqIn = win.width * win.height;
    const sqFt = sqIn / 144;
    const basePrice = calculateBasePrice(win.width, win.height, productLine);
    const adderCost = calculateAdderTotal(win.adders);
    const unitPrice = basePrice + adderCost;
    const lineTotal = unitPrice * win.qty;
    const adderList = Object.entries(win.adders).filter(([, v]) => v.checked).map(([name]) => name);
    const adders = adderList.length === 0 ? 'None' : adderList.length === 1 ? adderList[0].slice(0, 12) : adderList.join(', ');
    const ops = win.operations && win.operations.length > 0 ? win.operations.map(abbrevOp).join(', ') : '—';
    const glass = win.glass || 'No';
    const adderStr = adderCost > 0 ? fmt(adderCost) : '$0.00';
    lines.push(`${win.qty}${T}${win.width}"×${win.height}"${T}${sqFt > 0 ? sqFt.toFixed(1) : '—'}${T}${ops}${T}${glass}${T}${adders}${T}${adderStr}${T}${fmt(lineTotal)}`);
  });
  lines.push('');
  lines.push(`Total Windows:${T}${totalWindowCount}`);
  lines.push(`Base Total:${T}${fmt(totalBasePrice)}`);
  lines.push(`Adders Total:${T}${fmt(totalAdderCost)}`);
  lines.push(`Subtotal:${T}${fmt(grandTotal)}`);
  if (discountPercent > 0) {
    lines.push(`Discount (${discountPercent.toFixed(1)}%):${T}−${fmt(discountAmount)}`);
  }
  lines.push(`Total Due:${T}${fmt(totalDue)}`);
  return lines.join('\n');
}
function buildCsv(props: ExportActionsProps): string {
  const {
    windows,
    productLine,
    totalBasePrice,
    totalAdderCost,
    grandTotal,
    discountPercent,
    discountAmount,
    totalDue,
    totalWindowCount,
    avgPerWindow
  } = props;
  const rows: string[] = [];
  rows.push(`Window Quote — ${productLine}`);
  rows.push(`Date,${new Date().toLocaleDateString('en-US')}`);
  rows.push('');
  rows.push('Qty,Width (in),Height (in),Sq Ft,Operation,Obscured,Add-Ons,Adder Cost,Line Total');
  const included = windows.filter(w => w.included);
  included.forEach(win => {
    const sqIn = win.width * win.height;
    const sqFt = sqIn / 144;
    const basePrice = calculateBasePrice(win.width, win.height, productLine);
    const adderCost = calculateAdderTotal(win.adders);
    const unitPrice = basePrice + adderCost;
    const lineTotal = unitPrice * win.qty;
    const adders = Object.entries(win.adders).filter(([, v]) => v.checked).map(([name]) => name).join('; ') || 'None';
    const ops = win.operations && win.operations.length > 0 ? win.operations.join('; ') : 'None';
    const glass = win.glass || 'No';
    rows.push(`${win.qty},${win.width},${win.height},${sqFt.toFixed(1)},"${ops}","${glass}","${adders}",${adderCost.toFixed(2)},${lineTotal.toFixed(2)}`);
  });
  rows.push('');
  rows.push(`Total Windows,${totalWindowCount}`);
  rows.push(`Base Total,${totalBasePrice.toFixed(2)}`);
  rows.push(`Adders Total,${totalAdderCost.toFixed(2)}`);
  rows.push(`Subtotal,${grandTotal.toFixed(2)}`);
  if (discountPercent > 0) {
    rows.push(`Discount (${discountPercent.toFixed(1)}%),-${discountAmount.toFixed(2)}`);
  }
  rows.push(`Total Due,${totalDue.toFixed(2)}`);
  rows.push(`Avg per Window,${avgPerWindow.toFixed(2)}`);
  return rows.join('\n');
}
export function ExportActions(props: ExportActionsProps) {
  const [copied, setCopied] = useState(false);
  async function handleCopyPlainText() {
    const plain = buildPlainText(props);
    try {
      await navigator.clipboard.writeText(plain);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = plain;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  }
  function handleDownloadCsv() {
    const csv = buildCsv(props);
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `window-quote-${props.productLine.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
  return <div className="flex flex-wrap items-center gap-3" data-id="element-28">
      <button type="button" onClick={handleCopyPlainText} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#2E5E3F] rounded-md hover:bg-[#244D33] transition-colors" data-id="element-29">
        {copied ? <>
            <CheckIcon className="w-4 h-4" data-id="element-30" />
            Copied!
          </> : <>
            <ClipboardCopyIcon className="w-4 h-4" data-id="element-31" />
            Copy Quote to Clipboard
          </>}
      </button>
      <button type="button" onClick={handleDownloadCsv} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#2E5E3F] bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-32">
        <DownloadIcon className="w-4 h-4" data-id="element-33" />
        Download CSV
      </button>
      {copied && <span className="text-xs text-gray-500" data-id="element-34">
          Paste into Go High Level's text editor
        </span>}
    </div>;
}